import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { navigation } from '../../data/navigation';

const DropdownPanel = ({ items, parentHref, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 8 }}
    transition={{ duration: 0.18, ease: 'easeOut' }}
    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden z-50"
    style={{ minWidth: items.length > 6 ? 480 : 260 }}
  >
    <div className={`p-4 ${items.length > 6 ? 'grid grid-cols-2 gap-1' : 'flex flex-col gap-1'}`}>
      {items.map((item) => (
        <Link
          key={item.href}
          to={item.href}
          onClick={onClose}
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-cygnet-dark/5 hover:text-cygnet-dark transition-colors group"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-cygnet-dark/30 group-hover:bg-cygnet-dark transition-colors shrink-0" />
          {item.name}
        </Link>
      ))}
    </div>
    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50">
      <Link
        to={parentHref}
        onClick={onClose}
        className="flex items-center gap-1.5 text-xs font-semibold text-cygnet-dark hover:underline"
      >
        View all <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  </motion.div>
);

const NavItem = ({ item }) => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const active = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 120);
  };

  if (!item.dropdown) {
    return (
      <Link
        to={item.href}
        className={`relative px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          active ? 'text-cygnet-dark' : 'text-gray-600 hover:text-cygnet-dark hover:bg-gray-50'
        }`}
      >
        {item.name}
        {active && (
          <motion.span
            layoutId="nav-underline"
            className="absolute bottom-0 left-3 right-3 h-0.5 bg-cygnet-dark rounded-full"
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
      </Link>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
          active ? 'text-cygnet-dark' : 'text-gray-600 hover:text-cygnet-dark hover:bg-gray-50'
        }`}
      >
        {item.name}
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-3.5 h-3.5" />
        </motion.span>
        {active && (
          <motion.span
            layoutId="nav-underline"
            className="absolute bottom-0 left-3 right-3 h-0.5 bg-cygnet-dark rounded-full"
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
          />
        )}
      </button>
      <AnimatePresence>
        {open && (
          <DropdownPanel
            items={item.dropdown}
            parentHref={item.href}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const DesktopNavMenu = () => (
  <div className="hidden lg:flex items-center gap-x-1">
    {navigation.map((item) => (
      <NavItem key={item.name} item={item} />
    ))}
  </div>
);

export default DesktopNavMenu;
